import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import type { ChatMessage, ChatNote, MessageClickHandler } from './chat-message.interface'
import { ChatMessageActions } from './chat-message-actions.atom'
import { ChatMessageContent } from './chat-message-content.atom'
import type { BubbleVariant } from './chat-message-item.helper'
import { ChatMessageSendingIndicator } from './chat-message-sending-indicator.atom'

interface ChatMessageRowActionsProps {
  message: ChatMessage | ChatNote
  bubbleVariant: BubbleVariant
  isRightAligned: boolean
  isPinned: boolean
  onPin?: (itemId: string) => void
  onClick?: MessageClickHandler
}

/**
 * Row containing message content bubble and contextual actions (pin, copy) or sending indicator.
 */
export const ChatMessageRowActions: FC<ChatMessageRowActionsProps> = ({
  message,
  bubbleVariant,
  isRightAligned,
  isPinned,
  onPin,
  onClick,
}): ReactNode => {
  const isSending = message.type === 'message' && message.isSending

  return (
    <div
      className={cn(
        'flex min-w-0 max-w-full items-end gap-1',
        isRightAligned ? 'flex-row-reverse' : 'flex-row'
      )}
    >
      <ChatMessageContent message={message} bubbleVariant={bubbleVariant} onClick={onClick} />
      {isSending ? (
        <ChatMessageSendingIndicator />
      ) : (
        <ChatMessageActions
          messageId={message.id}
          content={message.content}
          isPinned={isPinned}
          isRightAligned={isRightAligned}
          onPin={onPin}
        />
      )}
    </div>
  )
}

ChatMessageRowActions.displayName = 'ChatMessageRowActions'
